import Input from "../../components/Input";
import Dropdown from "../../components/Dropdown";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProfileImageUploader from "../../components/ProfileImageLoader";
import { managerList, userState } from "../user/userSlice";
import useAuth from "../../hooks/useAuth";
import API_CONFIG from "../../config/apiConfig";

import { formatLabel, extractUserFromToken, ROLES } from "../../uitls/Utils";
import { useNavigate } from "react-router-dom";
import {
  updateUser,
  getManagersByRoleId,
  getUserById,
} from "../../api/userApi";
import { getAllDepartment } from "../../api/departmentApi";
import { getDepartment } from "../department/departmentSlice";
import Loader from "../../components/Loader";

const EmployeeProfile = () => {
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const allDepartments = useSelector(getDepartment);
  const managers = useSelector(managerList);

  const stateOfUser = useSelector(userState);

  const [profileImage, setProfileImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formValues, setFormValues] = useState({
    departmentId: null,
    userRoleId: "",
    designation: null,
    managerId: null,
    dateOfJoining: "",
    userName: "",
    corporateEmail: "",
    password: "",
    allowEmpLogin: true,
    emailNotification: true,
    firstName: "",
    middleName: "",
    lastName: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [jobTitle, setJobTitle] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);

  const [validPwd, setValidPwd] = useState(true);

  const [pwd] = useState("");
  const [matchPwd] = useState("");

  const [validMatch, setValidMatch] = useState(false);

  const [edit, setEdit] = useState(false);

  const navigate = useNavigate();

  const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!*@#$%]).{8,24}$/;

  let userDetails = auth?.user || stateOfUser?.user;

  if (!userDetails) {
    const token =
      auth?.token || stateOfUser?.token || localStorage.getItem("token");
    if (token) {
      console.log("Extracting user from token in EmployeeProfile...");
      userDetails = extractUserFromToken(token);
      if (userDetails) {
        localStorage.setItem("user", JSON.stringify(userDetails));
      }
    }
  }

  // Fetch user data if missing (failsafe for MFA flow or refresh)
  useEffect(() => {
    if (!userDetails && localStorage.getItem("token")) {
      // Token exists but no user data - fetch from localStorage user id
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.id) {
            dispatch(getUserById(parsedUser.id));
          }
        } catch (error) {
          console.error("Error parsing stored user:", error);
        }
      }
    }
  }, [userDetails, dispatch]);

  const currentRoleId = 
    localStorage.getItem("roleId") ||
    auth?.roleId ||
    auth?.user?.userRoleId ||
    userDetails?.userRoleId ||
    formValues?.userRoleId;
  
  const isAdmin = currentRoleId === ROLES.admin;

  const requiredInputFields = {
    firstName: true,
    corporateEmail: true,
    middleName: false,
    lastName: true,
    dateOfJoining: true,
    ...(!isAdmin && {
      departmentId: true,
      designation: true,
      managerId: false,
    }),
  };

  useEffect(() => {
    dispatch(getManagersByRoleId("role_C$VKrq_+$z"));
    dispatch(getAllDepartment());
  }, [dispatch]);

  useEffect(() => {
    if (userDetails?.id && allDepartments?.length > 0 && managers?.length > 0) {
      dispatch(getUserById(userDetails.id))
        .then((response) => {
          const user = response.payload;

          const userDept = user?.departmentId
            ? {
                label: user.departmentId?.departmentName || "",
                value: user.departmentId?.id || "",
              }
            : null;

          const findDesignation = userDept
            ? allDepartments
                .find((dept) => dept.id === user.departmentId.id)
                ?.designations.find((desig) => desig.id === user.designation)
            : null;

          const userDesignation = findDesignation
            ? { label: findDesignation.name, value: findDesignation.id }
            : null;

          const managerObj = managers.find((m) => m.id === user.managerId);
          const userManager = managerObj
            ? { label: managerObj.firstName || "N/A", value: managerObj.id }
            : { label: "N/A", value: "N/A" };

          const finalFormValues = {
            ...user,
            departmentId: userDept,
            designation: userDesignation,
            managerId: userManager,
            password: null,
          };

          setFormValues(finalFormValues);
        })
        .catch((error) => console.log("Error fetching user data: ", error));
    }
  }, [userDetails?.id, dispatch, allDepartments, managers]);

  useEffect(() => {
    const result = PWD_REGEX.test(matchPwd);
    if (result && validMatch) {
      setFormValues((prevValues) => ({ ...prevValues, password: matchPwd }));
      setFormErrors((prevValues) => ({ ...prevValues, password: "" }));
    } else {
      setFormValues((prevValues) => ({ ...prevValues, password: undefined }));
    }
  }, [pwd, matchPwd, validMatch]);

  useEffect(() => {
    const result = PWD_REGEX.test(pwd);
    setValidPwd(result);
    const match = pwd === matchPwd;
    setValidMatch(match);
  }, [pwd, matchPwd]);

  // Handle profile image preview
  useEffect(() => {
    if (profileImage && profileImage instanceof File) {
      const url = URL.createObjectURL(profileImage);
      setPreviewUrl(url);
      // Cleanup URL when component unmounts or image changes
      return () => URL.revokeObjectURL(url);
    } else if (formValues?.profileImage) {
      // Use existing profile image from server
      setPreviewUrl(API_CONFIG.getImageUrl(formValues.profileImage));
    } else if (userDetails?.profileImage) {
      setPreviewUrl(API_CONFIG.getImageUrl(userDetails.profileImage));
    } else {
      setPreviewUrl(null);
    }
  }, [profileImage, formValues?.profileImage, userDetails?.profileImage]);

  useEffect(() => {
    if (formValues?.departmentId?.value && allDepartments?.length > 0) {
      const selectedDept = allDepartments.find(
        (dept) => dept.id === formValues.departmentId.value
      );
      const designations =
        selectedDept?.designations?.map((job) => ({
          value: job.id,
          label: job.name,
        })) || [];

      setJobTitle(designations);
    } else {
      setJobTitle([]);
    }
  }, [formValues?.departmentId?.value, allDepartments]);

  useEffect(() => {
    const deptOptions =
      allDepartments?.map((dept) => ({
        value: dept.id,
        label: dept.departmentName,
      })) || [];

    const filteredManagers =
      managers
        ?.filter((m) => !m.isDeleted)
        .map((m) => ({
          value: m.id,
          label: m.firstName || "N/A",
        })) || [];

    setBusinessUnitOptions(deptOptions);
    setManagerOptions(filteredManagers);
  }, [allDepartments, managers]);

  const goBack = () => {
    setFormErrors({});
    setEdit(false);
    navigate("/user-profile");
  };

  const handleDropdownChange = (name) => (value) => {
    if (name === "departmentId") {
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
        designation: null, // Clear designation when department changes
      }));
    } else {
      setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value ? "" : "This field is required",
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const trimmedValue = type === "text" ? value.trim() : value;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: type === "checkbox" ? checked : value,
    }));

    let errorMessage = "";
    if (requiredInputFields[name]) {
      if (trimmedValue) {
        errorMessage = "";
      } else {
        errorMessage = `${formatLabel(name)} is required`;
      }
    } else {
      errorMessage = "";
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  const validateEmail = (value, key) => {
    const emailFields = ["corporateEmail", "email", "personalEmail"];
    if (emailFields.includes(key) && value) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(value) ? "" : "Invalid email format";
    }
    return "";
  };

  const validatePhoneNumber = (value, key) => {
    const phoneFields = ["phoneNumber", "contactNumber"];
    if (phoneFields.includes(key) && value) {
      return value.length === 10 ? "" : "Phone Number must have 10 Digits!";
    }
    return "";
  };

  const validatePassword = (key) => {
    return key === "password" && !validPwd ? "Enter a valid Password" : "";
  };

  const validateStringField = (value, key) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return `${formatLabel(key)} is required`;
    }

    const emailError = validateEmail(trimmedValue, key);
    if (emailError) return emailError;

    const phoneError = validatePhoneNumber(trimmedValue, key);
    if (phoneError) return phoneError;

    const passwordError = validatePassword(key);
    if (passwordError) return passwordError;

    return "";
  };

  const validateNonStringField = (value, key) => {
    if (value === null || value === undefined || value === "") {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  const validateField = (key) => {
    const value = formValues[key];

    if (!requiredInputFields[key]) {
      return "";
    }

    if (typeof value === "string") {
      return validateStringField(value, key);
    }

    return validateNonStringField(value, key);
  };

  const validateForm = () => {
    const validationErrors = {};

    Object.keys(requiredInputFields).forEach((key) => {
      const error = validateField(key);
      if (error) {
        validationErrors[key] = error;
      }
    });

    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const handleUpdate = () => {
    if (validateForm()) {
      const updatedFormValues = {
        userRoleId: auth?.user?.userRoleId,
        password: matchPwd && validPwd ? matchPwd : null,
        firstName: formValues?.firstName || "",
        middleName: formValues?.middleName || "",
        lastName: formValues?.lastName || "",
        corporateEmail: formValues?.corporateEmail || "",
        dateOfJoining: formValues?.dateOfJoining || "",
        allowEmpLogin: formValues?.allowEmpLogin,
        emailNotification: formValues?.emailNotification,
        userName: formValues?.userName,
      };

      if (!isAdmin) {
        updatedFormValues.deptId = formValues?.departmentId?.value || null;
        updatedFormValues.designation = formValues?.designation?.value || null;
        updatedFormValues.managerId = formValues?.managerId?.value || null;
      }

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      
      // Add user details as plain JSON string (not Blob)
      // Spring Boot @RequestPart can parse plain strings directly
      formData.append("userDetails", JSON.stringify(trimObjectValues(updatedFormValues)));

      // Add profile image file if selected
      if (profileImage && profileImage instanceof File) {
        formData.append("profileImage", profileImage);
      }

      dispatch(
        updateUser({
          userId: userDetails?.id,
          values: formData,
        })
      )
        .unwrap()
        .then(() => {
          // Refresh user data to get updated profile image
          dispatch(getUserById(userDetails?.id))
            .then((response) => {
              const user = response.payload;
              console.log("Updated user data:", user);
              console.log("Profile image path:", user?.profileImage);
              if (user?.profileImage) {
                const imageUrl = API_CONFIG.getImageUrl(user.profileImage);
                console.log("Full image URL:", imageUrl);
                setFormValues((prev) => ({
                  ...prev,
                  profileImage: user.profileImage,
                }));
                // Update preview URL with new image path immediately
                setPreviewUrl(imageUrl);
                
                // Update localStorage user data so header image updates immediately
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                  try {
                    const parsedUser = JSON.parse(storedUser);
                    parsedUser.profileImage = user.profileImage;
                    localStorage.setItem("user", JSON.stringify(parsedUser));
                  } catch (error) {
                    console.error("Error updating localStorage user:", error);
                  }
                }
              } else {
                // Clear preview if no image
                setPreviewUrl(null);
              }
              // Clear the file reference after successful upload
              setProfileImage(null);
            })
            .catch((error) => {
              console.error("Error fetching updated user:", error);
            });
          setEdit(false); // Exit edit mode instead of navigating away
        })
        .catch((error) => {
          console.error("Error updating user profile:", error);
        });
    }
  };

  return (
    <>
      {stateOfUser?.loading && <Loader />}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Enhanced Profile Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden relative border border-gray-100">
            {/* Header Section with Gradient */}
            <div className="relative bg-gradient-to-r from-[#0b3a5c] via-[#135e96] to-[#0b3a5c] p-8">
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}
              ></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">
                      My Profile
                    </h3>
                    <p className="text-blue-100 text-sm font-medium">
                      Personal and professional information
                    </p>
                  </div>
                </div>
                {!edit && (
                  <button
                    onClick={() => setEdit(true)}
                    className="px-6 py-3 bg-white text-[#0b3a5c] rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center gap-2 group"
                  >
                    <svg
                      className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Profile
                  </button>
                )}
              </div>
            </div>

            {/* Profile Picture and Info Section */}
            <div className="relative bg-gradient-to-br from-white via-blue-50/20 to-white p-8 md:p-10">
              <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-10 mb-10">
                {/* Enhanced Profile Picture */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0b3a5c] via-[#135e96] to-[#0b3a5c] rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                  <div className="relative w-40 h-40 rounded-full bg-gradient-to-br from-[#0b3a5c] via-[#135e96] to-[#0b3a5c] p-1.5 shadow-2xl transform group-hover:scale-105 transition-transform duration-300">
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden ring-4 ring-white/50">
                      <ProfileImageUploader 
                        setProfileImage={setProfileImage} 
                        existingImage={previewUrl}
                      />
                    </div>
                  </div>
                  {edit && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-[#0b3a5c] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                      Click to change
                    </div>
                  )}
                </div>

                {/* User Info Section */}
                <div className="flex-1 text-center md:text-left space-y-3">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#0b3a5c] to-[#135e96] bg-clip-text text-transparent mb-2">
                      {formValues?.firstName ||
                        formValues?.userName ||
                        "Employee"}{" "}
                      {formValues?.lastName || ""}
                    </h2>
                    {!isAdmin && (
                      <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-full border border-blue-200">
                        <svg
                          className="w-4 h-4 text-[#135e96]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm font-semibold text-[#135e96]">
                          {formValues?.designation?.label || "Employee"}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-2 text-gray-600">
                    <svg
                      className="w-5 h-5 text-[#135e96]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-sm font-medium">
                      {formValues?.corporateEmail}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-sm text-gray-500 font-medium">
                    Profile Details
                  </span>
                </div>
              </div>

              {/* Form Fields Grid */}
              <div className={`grid grid-cols-1 md:grid-cols-2 ${!isAdmin ? 'xl:grid-cols-3' : ''} gap-6`}>
                <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                  <div className="relative">
                    <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-[#0b3a5c] to-[#135e96] rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                    <Input
                      name="corporateEmail"
                      placeholder="Enter email"
                      value={formValues?.corporateEmail}
                      onChange={handleInputChange}
                      label="Organizational Email"
                      requiredField={true}
                      error={formErrors?.corporateEmail}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    name="firstName"
                    placeholder="Enter first name"
                    value={formValues?.firstName}
                    onChange={handleInputChange}
                    label="First Name"
                    requiredField={true}
                    error={formErrors?.firstName}
                    readOnly={!edit}
                  />
                </div>
                <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    name="middleName"
                    placeholder="Enter middle name"
                    value={formValues?.middleName}
                    onChange={handleInputChange}
                    label="Middle Name"
                    requiredField={false}
                    error={formErrors?.middleName}
                    readOnly={!edit}
                  />
                </div>
                <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    name="lastName"
                    placeholder="Enter last name"
                    value={formValues?.lastName}
                    onChange={handleInputChange}
                    label="Last Name"
                    requiredField={true}
                    error={formErrors?.lastName}
                    readOnly={!edit}
                  />
                </div>
                {!isAdmin && (
                  <>
                    <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                      <Dropdown
                        submitRef={true}
                        defaultValue={formValues?.departmentId || null}
                        options={businessUnitOptions || []}
                        handleChange={handleDropdownChange("departmentId")}
                        errorMessage={formErrors?.departmentId || ""}
                        requiredField={true}
                        defaultLabel="Select Business Unit"
                        label="Business Unit"
                        disabled={true}
                      />
                    </div>
                    <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                      <Dropdown
                        submitRef={true}
                        defaultValue={formValues?.designation || null}
                        options={jobTitle || []}
                        handleChange={handleDropdownChange("designation")}
                        errorMessage={formErrors?.designation || ""}
                        requiredField={jobTitle && jobTitle.length > 0}
                        defaultLabel={
                          !formValues?.departmentId?.value
                            ? "Select business unit first"
                            : "Select Designation"
                        }
                        label="Job Title"
                        disabled={!edit}
                      />
                    </div>
                    <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                      <Dropdown
                        submitRef={true}
                        defaultValue={
                          formValues?.managerId?.label
                            ? formValues.managerId
                            : { label: "N/A", value: "N/A" }
                        }
                        options={managerOptions}
                        handleChange={handleDropdownChange("managerId")}
                        errorMessage={formErrors?.managerId || ""}
                        requiredField={false}
                        defaultLabel={formErrors?.managerId || "N/A"}
                        label="Manager"
                        disabled={true}
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2 transform transition-all duration-300 hover:scale-[1.02]">
                  <Input
                    name="dateOfJoining"
                    placeholder="Select date"
                    type="date"
                    value={formValues?.dateOfJoining || ""}
                    onChange={handleInputChange}
                    label="Date of Joining"
                    requiredField={true}
                    error={formErrors?.dateOfJoining || ""}
                    disabled={true}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Action Buttons */}
          {edit && (
            <div className="flex flex-col sm:flex-row justify-end gap-4 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
              <button
                onClick={goBack}
                className="px-8 py-3.5 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={stateOfUser?.loading}
                className="px-8 py-3.5 brand-btn-primary text-white rounded-xl font-semibold transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                {stateOfUser?.loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>Update Profile</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeProfile;
